import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Clock, FileText, MessageSquare, AlertTriangle, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  format, startOfMonth, endOfMonth, eachDayOfInterval, 
  isSameMonth, isSameDay, addMonths, subMonths, isToday,
  startOfWeek, endOfWeek, parseISO
} from 'date-fns';

const DATE_TYPES = [
  { value: 'scheduled_date', label: 'Assessment Session', icon: Clock, color: 'bg-blue-500' },
  { value: 'parent_session_date', label: 'Parent Session', icon: MessageSquare, color: 'bg-indigo-500' },
  { value: 'observation_date', label: 'Observation Session', icon: CalendarIcon, color: 'bg-amber-500' },
  { value: 'feedback_date', label: 'Feedback Session', icon: MessageSquare, color: 'bg-emerald-500' },
];

const CalendarView = () => {
  const [clients, setClients] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('all'); // all, scheduled, due, feedback
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleClient, setScheduleClient] = useState('');
  const [scheduleDateType, setScheduleDateType] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle scheduling a date for a client
  const handleScheduleDate = async () => {
    if (!scheduleClient || !scheduleDateType || !selectedDate) {
      toast.error('Please select a client and date type');
      return;
    }

    setSaving(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const updates = { [scheduleDateType]: dateStr };
      
      // Auto-update status based on date type
      if (scheduleDateType === 'scheduled_date') {
        updates.status = 'Assessment Scheduled';
      } else if (scheduleDateType === 'feedback_date') {
        updates.status = 'Follow-up Booked';
      }
      // parent_session_date and observation_date don't change status
      
      await clientsAPI.update(scheduleClient, updates);
      toast.success('Date scheduled successfully');
      setShowScheduleDialog(false);
      setScheduleClient('');
      setScheduleDateType('');
      loadClients(); // Refresh the calendar
    } catch (error) {
      toast.error('Failed to schedule date');
    } finally {
      setSaving(false);
    }
  };

  // Open schedule dialog
  const openScheduleDialog = () => {
    if (!selectedDate) {
      toast.error('Please select a date first');
      return;
    }
    setShowScheduleDialog(true);
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const events = [];

    clients.forEach(client => {
      if (client.scheduled_date === dateStr) {
        events.push({
          type: 'scheduled',
          client,
          label: 'Assessment Session',
          icon: Clock,
          color: 'bg-blue-500'
        });
      }
      if (client.parent_session_date === dateStr) {
        events.push({
          type: 'parent',
          client,
          label: 'Parent Session',
          icon: MessageSquare,
          color: 'bg-indigo-500'
        });
      }
      if (client.observation_date === dateStr) {
        events.push({
          type: 'observation',
          client,
          label: 'Observation Session',
          icon: CalendarIcon,
          color: 'bg-amber-500'
        });
      }
      if (client.report_due_date === dateStr) {
        const isOverdue = new Date(dateStr) < new Date();
        events.push({
          type: 'due',
          client,
          label: isOverdue ? 'Report Overdue' : 'Report Due',
          icon: isOverdue ? AlertTriangle : FileText,
          color: isOverdue ? 'bg-red-500' : 'bg-orange-500'
        });
      }
      if (client.feedback_date === dateStr) {
        events.push({
          type: 'feedback',
          client,
          label: 'Feedback Session',
          icon: MessageSquare,
          color: 'bg-emerald-500'
        });
      }
      if (client.assessment_completion_date === dateStr) {
        events.push({
          type: 'completed',
          client,
          label: 'Assessment Completed',
          icon: CalendarIcon,
          color: 'bg-purple-500'
        });
      }
    });

    // Filter by view type
    if (viewType !== 'all') {
      return events.filter(e => e.type === viewType);
    }
    return events;
  };

  // Get calendar days
  const getCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const days = getCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get events for selected date
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  // Count events by type for the month
  const getMonthStats = () => {
    let scheduled = 0, due = 0, feedback = 0, overdue = 0;
    const today = new Date();
    
    clients.forEach(client => {
      if (client.scheduled_date) {
        const d = parseISO(client.scheduled_date);
        if (isSameMonth(d, currentMonth)) scheduled++;
      }
      if (client.report_due_date) {
        const d = parseISO(client.report_due_date);
        if (isSameMonth(d, currentMonth)) {
          due++;
          if (d < today && client.status !== 'Report Sent' && client.status !== 'File Closed') {
            overdue++;
          }
        }
      }
      if (client.feedback_date) {
        const d = parseISO(client.feedback_date);
        if (isSameMonth(d, currentMonth)) feedback++;
      }
    });
    
    return { scheduled, due, feedback, overdue };
  };

  const monthStats = getMonthStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope']">Calendar</h1>
          <p className="text-slate-500 mt-1">View scheduled assessments and deadlines</p>
        </div>
        <div className="flex gap-2">
          <Select value={viewType} onValueChange={setViewType}>
            <SelectTrigger className="w-40" data-testid="calendar-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="due">Due Dates</SelectItem>
              <SelectItem value="feedback">Feedback</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Month Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <div>
                <p className="text-2xl font-semibold">{monthStats.scheduled}</p>
                <p className="text-xs text-slate-500">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <div>
                <p className="text-2xl font-semibold">{monthStats.due}</p>
                <p className="text-xs text-slate-500">Due Dates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <div>
                <p className="text-2xl font-semibold">{monthStats.feedback}</p>
                <p className="text-xs text-slate-500">Feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div>
                <p className="text-2xl font-semibold">{monthStats.overdue}</p>
                <p className="text-xs text-slate-500">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 border border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-['Manrope']">
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex gap-1">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setCurrentMonth(new Date())}
                >
                  Today
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Week day headers */}
            <div className="grid grid-cols-7 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, idx) => {
                const events = getEventsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isTodayDate = isToday(day);

                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      relative min-h-20 p-1 rounded-lg border transition-all text-left
                      ${isCurrentMonth ? 'bg-white' : 'bg-slate-50'}
                      ${isSelected ? 'border-slate-800 ring-1 ring-slate-800' : 'border-slate-100 hover:border-slate-300'}
                      ${isTodayDate ? 'bg-blue-50' : ''}
                    `}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <span className={`
                      text-sm font-medium
                      ${isCurrentMonth ? 'text-slate-900' : 'text-slate-400'}
                      ${isTodayDate ? 'text-blue-600' : ''}
                    `}>
                      {format(day, 'd')}
                    </span>
                    
                    {/* Event dots */}
                    {events.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {events.slice(0, 3).map((event, i) => (
                          <div 
                            key={i}
                            className={`w-2 h-2 rounded-full ${event.color}`}
                            title={`${event.client.name} - ${event.label}`}
                          />
                        ))}
                        {events.length > 3 && (
                          <span className="text-xs text-slate-500">+{events.length - 3}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-slate-600">Scheduled</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-slate-600">Due Date</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-slate-600">Feedback</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-slate-600">Completed</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-slate-600">Overdue</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <Card className="border border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-['Manrope']">
                {selectedDate 
                  ? format(selectedDate, 'EEEE, MMMM d') 
                  : 'Select a date'}
              </CardTitle>
              {selectedDate && (
                <Button 
                  size="sm" 
                  onClick={openScheduleDialog}
                  data-testid="schedule-date-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Schedule
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <p className="text-slate-500 text-sm">Click on a date to view events or schedule assessments</p>
            ) : selectedDateEvents.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm mb-3">No events on this date</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={openScheduleDialog}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Schedule Assessment
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map((event, idx) => {
                  const Icon = event.icon;
                  return (
                    <div 
                      key={idx}
                      className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/clients/${event.client.id}`)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg ${event.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {event.client.name}
                          </p>
                          <p className="text-sm text-slate-500">{event.label}</p>
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {event.client.assessment_type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Schedule Date Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Schedule Assessment</DialogTitle>
            <DialogDescription>
              Schedule an assessment for {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Client</Label>
              <Select value={scheduleClient} onValueChange={setScheduleClient}>
                <SelectTrigger data-testid="schedule-client-select">
                  <SelectValue placeholder="Choose a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients
                    .filter(c => c.status !== 'File Closed')
                    .map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.assessment_type}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date Type</Label>
              <Select value={scheduleDateType} onValueChange={setScheduleDateType}>
                <SelectTrigger data-testid="schedule-type-select">
                  <SelectValue placeholder="What are you scheduling?" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {scheduleClient && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Selected client:</p>
                <p className="font-medium">
                  {clients.find(c => c.id === scheduleClient)?.name}
                </p>
                <p className="text-sm text-slate-500">
                  Current status: {clients.find(c => c.id === scheduleClient)?.status}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowScheduleDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleScheduleDate}
              disabled={saving || !scheduleClient || !scheduleDateType}
              data-testid="confirm-schedule-btn"
            >
              {saving ? 'Scheduling...' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarView;
